import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Privacy Policy</h1>
            <p className="text-xs text-slate-400">Last updated: March 2026</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6 text-sm text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">1. Introduction</h2>
            <p>GYNOVATE AI/SI ("we", "our", "the Service") is a clinical decision-support tool designed for licensed medical professionals. This Privacy Policy describes how we collect, use, and protect information when you use our Service. We are committed to maintaining the privacy and security of all data in compliance with applicable healthcare data protection regulations including HIPAA (Health Insurance Portability and Accountability Act).</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">2. No Patient Health Information (PHI) Storage</h2>
            <p><strong>GYNOVATE AI/SI does not store, retain, or process Protected Health Information (PHI).</strong> The Service is designed as a clinical reference tool. Users are advised not to enter any patient-identifiable information including but not limited to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Patient names, dates of birth, or contact details</li>
              <li>Medical record numbers or insurance IDs</li>
              <li>Social Security numbers or Aadhaar numbers</li>
              <li>Any combination of data that could identify a specific patient</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">3. Data We Collect</h2>
            <p>We collect only the minimum data necessary to provide the Service:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Chat session data:</strong> Clinical queries and AI responses for session continuity. These are automatically deleted after 24 hours.</li>
              <li><strong>Usage analytics:</strong> Anonymous, aggregated data about feature usage (no personal identifiers).</li>
              <li><strong>Cookie preferences:</strong> Your consent choices for cookies.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">4. Automatic Data Deletion</h2>
            <p>All chat sessions and associated messages are <strong>automatically purged every 24 hours</strong>. No conversation history is retained beyond this period. Favourited responses are retained until manually deleted by the user.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">5. Data Security</h2>
            <p>We implement industry-standard security measures:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>All data in transit is encrypted via TLS/SSL (HTTPS)</li>
              <li>Database access is restricted and authenticated</li>
              <li>No PHI is stored at any point in our infrastructure</li>
              <li>AI model providers process queries without retaining data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">6. Third-Party AI Services</h2>
            <p>Clinical queries are processed by third-party AI models (OpenAI, Anthropic). These services:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Do not use your queries to train their models</li>
              <li>Process data transiently and do not retain it</li>
              <li>Maintain their own HIPAA-compliant data handling policies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">7. Cookies</h2>
            <p>We use essential cookies only for session management and user preferences (language, theme). No third-party tracking cookies are used. You can manage cookie preferences at any time.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Request deletion of any data associated with your usage</li>
              <li>Opt out of non-essential cookies</li>
              <li>Contact us with privacy-related concerns</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">9. Disclaimer</h2>
            <p>GYNOVATE AI/SI provides clinical decision support only. It does not replace professional medical judgement. The Service is intended for use by licensed healthcare professionals only. Always verify AI-generated recommendations against current clinical guidelines and your professional expertise.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-slate-900 mb-2">10. Contact</h2>
            <p>For privacy inquiries, contact: <strong>Dr. Vikram Dukle, Candolim, Goa, India</strong></p>
            <p>Website: www.gynovate.in</p>
          </section>
        </div>
      </div>
    </div>
  );
}
